import { useFrame } from '@react-three/fiber';
import { useEffect, useState } from 'react';
import { useSnapshot } from 'valtio';

import defaultVerticalOffset from '@cartwhl/3d/gltf/defaultVerticalOffsets';
import { EnhancedGLTF } from '@cartwhl/3d/gltf/types';
import useFrameMixer from '@cartwhl/3d/r3f/hooks/useFrameMixer';
import { HAND_OPTIONS, RotationOffsets } from '@cartwhl/common/types';

import useCanvasRecorder from 'hooks/useCanvasRecorder';
import { useHasActiveSession } from 'hooks/useHasActiveSession';
import useLoadAnimation, { AnimationData } from 'hooks/useLoadAnimation';
import useLoadCharacter from 'hooks/useLoadCharacter';
import {
  animationGenerationStore,
  animationPlayerStore,
  sceneSettingsStore,
} from 'stores';
import { USE_DEFAULT_THUMBNAIL_CAMERA_QUERY_PARAM_NAME } from 'stores/sceneSettingsStore';

export default function StageFromBVH() {
  const params = new URLSearchParams(window.location.search);
  const isThumbnail =
    params.get(USE_DEFAULT_THUMBNAIL_CAMERA_QUERY_PARAM_NAME) === 'true';

  const { animationPaths } = useSnapshot(animationGenerationStore.state);
  const {
    cameraSettings: { cameraFollow, serializedCamera },
    characterSettings: { charUploadID, staticHands, enableRootMotion },
  } = useSnapshot(sceneSettingsStore.state);

  const { trimFrameStart, trimFrameEnd } = useSnapshot(
    animationPlayerStore.state,
  );

  const hasActiveSession = useHasActiveSession();

  const characterRootMotion = isThumbnail ? false : enableRootMotion;
  const yOffset = hasActiveSession ? 0 : defaultVerticalOffset(charUploadID);

  const setLoading =
    (key: 'data' | 'animation' | 'mesh') => (loading: boolean) => {
      animationGenerationStore.setLoading(key, loading);
    };

  const { meshData, rotationOffsets } = useLoadCharacter(
    false,
    setLoading('mesh'),
  );
  const animations = useLoadAnimation(
    animationPaths as Array<string>, // recast without readonly
    setLoading('animation'),
    {
      staticHands: staticHands as HAND_OPTIONS,
      enableRootMotion: [characterRootMotion, characterRootMotion, true],
      trimFrameStart,
      trimFrameEnd,
      yOffset,
      rotationOffsets: rotationOffsets as RotationOffsets,
    },
  );

  if (!meshData || !animations || !animations[0]) return null;

  return (
    <Stage
      animations={animations}
      cameraFollow={cameraFollow}
      meshData={meshData}
      serializedCamera={serializedCamera}
    />
  );
}

function Stage({
  animations,
  cameraFollow,
  meshData,
  serializedCamera,
}: {
  animations: Array<AnimationData>;
  cameraFollow: boolean;
  meshData: EnhancedGLTF;
  serializedCamera: string;
}) {
  const [animData, setAnimData] = useState<AnimationData>(animations[0]);

  const { autoPlayOnLoad, isRecording } = useSnapshot(
    animationPlayerStore.state,
  );

  const {
    backgroundSettings: {
      canvasHeight,
      canvasWidth,
      useBackgroundGradient,
      hexColor,
    },
    playbackSettings: { frameRate, playbackSpeed, frameStepSize },
  } = useSnapshot(sceneSettingsStore.state);

  // Swop to a new clip when the last one ends
  const onLoopFinish = () => {
    if (animations.length > 0) {
      const idx = (animations.indexOf(animData) + 1) % animations.length;
      setAnimData(animations[idx]);
    }
  };

  const mixer = useFrameMixer(animData.animationData.clip, meshData.scene, {
    frameRate,
    frameStepSize,
    playbackSpeed,
    autoplay: autoPlayOnLoad,
    onLoopFinish,
    onReady: () => animationPlayerStore.setMixerReady(true),
    onPlayChange: animationPlayerStore.setIsPlaying,
    onFrameUpdate: (frame) => {
      if (!isRecording) animationPlayerStore.setCurrentFrame(frame);
    },
  });

  useEffect(() => {
    if (mixer) {
      animationPlayerStore.setDuration({
        ...mixer.duration,
        ...animData.originalDuration,
      });
      animationPlayerStore.hoistAnimationMixer(mixer);
    }
  }, [mixer, mixer.duration, animData.originalDuration]);

  const canvasRecorder = useCanvasRecorder(mixer, {
    width: canvasWidth,
    height: canvasHeight,
    frameRate,
    playbackSpeed,
    backgroundColor: useBackgroundGradient ? undefined : hexColor,
  });

  useEffect(() => {
    if (canvasRecorder) {
      animationPlayerStore.setCanvasRecorder(canvasRecorder);
    }
  }, [canvasRecorder]);

  // When new animations are passed in, reset state to first animation
  useEffect(() => {
    setAnimData(animations[0]);
  }, [animations]);

  useFrame((state, delta) => {
    if (!isRecording) {
      mixer.update(delta);
    }
    animationPlayerStore.updateCamera({
      cameraFollow,
      delta,
      serializedCamera,
      target: state.scene.getObjectByName('pelvis'),
    });
  });

  return <primitive object={meshData.scene} rotation={[-Math.PI / 2, 0, 0]} />;
}
